// MathML → LaTeX converter for Natural Math Editor Canvas LTI
// Ported from the naturalmatheditor.com web editor export implementation.

const GREEK: Record<string, string> = {
  'α': '\\alpha', 'β': '\\beta', 'γ': '\\gamma', 'δ': '\\delta',
  'ε': '\\epsilon', 'ζ': '\\zeta', 'η': '\\eta', 'θ': '\\theta',
  'ι': '\\iota', 'κ': '\\kappa', 'λ': '\\lambda', 'μ': '\\mu',
  'ν': '\\nu', 'ξ': '\\xi', 'π': '\\pi', 'ρ': '\\rho',
  'σ': '\\sigma', 'τ': '\\tau', 'υ': '\\upsilon', 'φ': '\\phi',
  'χ': '\\chi', 'ψ': '\\psi', 'ω': '\\omega',
  'Α': 'A', 'Β': 'B', 'Γ': '\\Gamma', 'Δ': '\\Delta',
  'Ε': 'E', 'Ζ': 'Z', 'Η': 'H', 'Θ': '\\Theta',
  'Ι': 'I', 'Κ': 'K', 'Λ': '\\Lambda', 'Μ': 'M',
  'Ν': 'N', 'Ξ': '\\Xi', 'Π': '\\Pi', 'Ρ': 'R',
  'Σ': '\\Sigma', 'Τ': 'T', 'Υ': '\\Upsilon', 'Φ': '\\Phi',
  'Χ': 'X', 'Ψ': '\\Psi', 'Ω': '\\Omega',
  'ϵ': '\\epsilon', 'ϑ': '\\vartheta', 'ϕ': '\\varphi',
  'ϱ': '\\varrho', 'ϖ': '\\varpi',
};

const NAMED_FN: Record<string, string> = {
  'sin': '\\sin', 'cos': '\\cos', 'tan': '\\tan',
  'cot': '\\cot', 'sec': '\\sec', 'csc': '\\csc',
  'arcsin': '\\arcsin', 'arccos': '\\arccos', 'arctan': '\\arctan',
  'sinh': '\\sinh', 'cosh': '\\cosh', 'tanh': '\\tanh',
  'ln': '\\ln', 'log': '\\log', 'exp': '\\exp',
  'lim': '\\lim', 'sup': '\\sup', 'inf': '\\inf',
  'max': '\\max', 'min': '\\min',
  'det': '\\det', 'dim': '\\dim', 'ker': '\\ker',
  'gcd': '\\gcd', 'deg': '\\deg', 'arg': '\\arg',
  'Pr': '\\Pr', 'hom': '\\hom',
};

const OP_MAP: Record<string, string> = {
  '+': '+', '-': '-', '=': '=', '<': '<', '>': '>',
  '≤': '\\leq', '≥': '\\geq', '≠': '\\neq',
  '≈': '\\approx', '∼': '\\sim', '≡': '\\equiv',
  '×': '\\times', '÷': '\\div', '·': '\\cdot',
  '±': '\\pm', '∓': '\\mp',
  '∞': '\\infty', '∅': '\\emptyset',
  '∈': '\\in', '∉': '\\notin',
  '⊂': '\\subset', '⊃': '\\supset',
  '⊆': '\\subseteq', '⊇': '\\supseteq',
  '∪': '\\cup', '∩': '\\cap',
  '∧': '\\wedge', '∨': '\\vee', '¬': '\\neg',
  '→': '\\to', '←': '\\leftarrow', '↔': '\\leftrightarrow',
  '⇒': '\\Rightarrow', '⇐': '\\Leftarrow', '⇔': '\\Leftrightarrow',
  '∑': '\\sum', '∏': '\\prod',
  '∫': '\\int', '∬': '\\iint', '∭': '\\iiint', '∮': '\\oint',
  '∂': '\\partial', '∇': '\\nabla',
  '√': '\\sqrt', '…': '\\ldots', '⋯': '\\cdots',
  '⌊': '\\lfloor', '⌋': '\\rfloor', '⌈': '\\lceil', '⌉': '\\rceil',
  '|': '|', '‖': '\\|',
  '(': '(', ')': ')', '[': '[', ']': ']', '{': '\\{', '}': '\\}',
  ',': ',', ';': ';', ':': ':', '!': '!',
  '∀': '\\forall', '∃': '\\exists',
  '°': '^{\\circ}', '′': "'", '″': "''",
};

const LIMITS_OPS: Record<string, boolean> = {
  '\\lim': true, '\\max': true, '\\min': true,
  '\\sup': true, '\\inf': true,
  '\\sum': true, '\\prod': true,
  '\\int': true, '\\iint': true, '\\iiint': true, '\\oint': true,
};

const ACCENT_MAP: Record<string, string> = {
  '̂': '\\hat', '̃': '\\tilde', '̄': '\\bar',
  '̇': '\\dot', '̈': '\\ddot',
  '→': '\\vec', '⃗': '\\vec',
};

function stripInvis(s: string): string {
  return s.replace(/⁡/g, '');
}

function childElements(el: Element): Element[] {
  return Array.from(el.childNodes).filter((n): n is Element => n.nodeType === 1);
}

function convertChildren(el: Element): string {
  return childElements(el).map(convertEl).join(' ');
}

function convertEl(el: Element): string {
  const ch = childElements(el);

  switch (el.localName) {
    case 'math':
    case 'mrow':
    case 'mstyle':
    case 'mpadded':
    case 'mphantom':
    case 'merror':
      return convertChildren(el);

    case 'mi': {
      const t = stripInvis(el.textContent ?? '');
      return NAMED_FN[t] ?? GREEK[t] ?? OP_MAP[t] ?? t;
    }

    case 'mn': {
      return stripInvis(el.textContent ?? '');
    }

    case 'mo': {
      const t = stripInvis(el.textContent ?? '');
      return OP_MAP[t] ?? GREEK[t] ?? NAMED_FN[t] ?? t;
    }

    case 'mtext': {
      return `\\text{${el.textContent ?? ''}}`;
    }

    case 'mspace': {
      return ' ';
    }

    case 'mfrac': {
      const [num, den] = ch;
      return `\\frac{${num ? convertEl(num) : ''}}{${den ? convertEl(den) : ''}}`;
    }

    case 'msqrt': {
      return `\\sqrt{${convertChildren(el)}}`;
    }

    case 'mroot': {
      const [base, index] = ch;
      return `\\sqrt[${index ? convertEl(index) : ''}]{${base ? convertEl(base) : ''}}`;
    }

    case 'msup': {
      const [base, sup] = ch;
      return `${base ? convertEl(base) : ''}^{${sup ? convertEl(sup) : ''}}`;
    }

    case 'msub': {
      const [base, sub] = ch;
      return `${base ? convertEl(base) : ''}_{${sub ? convertEl(sub) : ''}}`;
    }

    case 'msubsup': {
      const [base, sub, sup] = ch;
      return `${base ? convertEl(base) : ''}_{${sub ? convertEl(sub) : ''}}^{${sup ? convertEl(sup) : ''}}`;
    }

    case 'munder': {
      const [base, under] = ch;
      const baseLatex = base ? convertEl(base) : '';
      const underLatex = under ? convertEl(under) : '';
      if (LIMITS_OPS[baseLatex]) return `${baseLatex}_{${underLatex}}`;
      return `\\underset{${underLatex}}{${baseLatex}}`;
    }

    case 'munderover': {
      const [base, under, over] = ch;
      const baseLatex = base ? convertEl(base) : '';
      return `${baseLatex}_{${under ? convertEl(under) : ''}}^{${over ? convertEl(over) : ''}}`;
    }

    case 'mover': {
      const [base, over] = ch;
      const overText = over?.textContent ?? '';
      const accentCmd = ACCENT_MAP[overText];
      if (accentCmd) return `${accentCmd}{${base ? convertEl(base) : ''}}`;
      return `\\overset{${over ? convertEl(over) : ''}}{${base ? convertEl(base) : ''}}`;
    }

    case 'mfenced': {
      const open = el.getAttribute('open') ?? '(';
      const close = el.getAttribute('close') ?? ')';
      const sep = el.getAttribute('separators') ?? ',';
      const openLatex = OP_MAP[open] ?? open;
      const closeLatex = OP_MAP[close] ?? close;
      const sepChar = sep[0] ?? ',';
      const inner = ch.map(c => convertEl(c)).join((OP_MAP[sepChar] ?? sepChar) + ' ');
      return `\\left${openLatex} ${inner} \\right${closeLatex}`;
    }

    case 'mtable': {
      const rows = ch.filter(c => c.localName === 'mtr');
      const body = rows.map(row => {
        const cells = childElements(row).filter(c => c.localName === 'mtd');
        return cells.map(cell => convertChildren(cell)).join(' & ');
      }).join(' \\\\ ');
      return `\\begin{matrix} ${body} \\end{matrix}`;
    }

    default:
      return convertChildren(el);
  }
}

/**
 * Convert a MathML string (as returned by editor.toMml()) to a LaTeX string.
 */
export function mathmlToLatex(mmlString: string): string {
  const doc = new DOMParser().parseFromString(mmlString, 'application/xml');
  const raw = convertEl(doc.documentElement);
  return raw.replace(/⁡/g, '').replace(/\s{2,}/g, ' ').trim();
}
